import { isTauriRuntime } from "./runtime";
import {
  createBrowserDeferredEncryptedMediaKeyEnvelope,
  prepareBrowserEncryptedBytesTransfer,
  prepareBrowserEncryptedMediaTransfer,
  receiveBrowserEncryptedBytesTransfer,
  receiveBrowserEncryptedMediaTransfer,
  uploadBrowserPreparedEncryptedMediaTransfer
} from "./browserSecureMedia";

export const encryptedRelayAlgorithm = "x25519-xchacha20poly1305-v1" as const;

export interface RelayRecipientDeviceKey {
  recipientDeviceId: string;
  recipientKeyId: string;
  publicKey: string;
}

export interface EncryptedMediaKeyEnvelope {
  recipientDeviceId: string;
  recipientKeyId: string;
  envelope: string;
}

export interface PreparedEncryptedMediaTransfer {
  sourceDeviceId: string;
  sourceSha256: string;
  ciphertextSha256: string;
  ciphertextSize: number;
  algorithm: typeof encryptedRelayAlgorithm;
  envelopes: EncryptedMediaKeyEnvelope[];
}

export interface UploadedEncryptedMediaTransfer {
  transferId: string;
  ciphertextSha256: string;
  ciphertextSize: number;
}

export interface ReceivedEncryptedBinaryTransfer {
  bytes: Uint8Array;
  sha256: string;
}

async function invoke<T>(command: string, payload: Record<string, unknown>) {
  const { invoke: nativeInvoke } = await import("@tauri-apps/api/core");
  return nativeInvoke<T>(command, payload);
}

export function prepareEncryptedMediaTransfer(
  transferId: string,
  mediaId: string,
  recipients: RelayRecipientDeviceKey[]
) {
  if (!isTauriRuntime()) return prepareBrowserEncryptedMediaTransfer(transferId, mediaId, recipients);
  return invoke<PreparedEncryptedMediaTransfer>("prepare_encrypted_media_transfer", {
    request: { transferId, mediaId, recipients }
  });
}

export function prepareEncryptedBinaryTransfer(
  transferId: string,
  contentId: string,
  bytes: Uint8Array,
  recipients: RelayRecipientDeviceKey[]
) {
  if (!isTauriRuntime()) return prepareBrowserEncryptedBytesTransfer(transferId, contentId, bytes, recipients);
  return invoke<PreparedEncryptedMediaTransfer>("prepare_encrypted_derived_transfer", {
    request: { transferId, contentId, bytes: Array.from(bytes), recipients }
  });
}

export function createDeferredEncryptedMediaKeyEnvelope(request: {
  mediaId: string;
  sourceEnvelope: string;
  recipient: RelayRecipientDeviceKey;
}) {
  if (!isTauriRuntime()) return createBrowserDeferredEncryptedMediaKeyEnvelope(request);
  return invoke<EncryptedMediaKeyEnvelope>("create_deferred_encrypted_media_key_envelope", { request });
}

export function uploadPreparedEncryptedMediaTransfer(transferId: string, uploadUrl: string) {
  if (!isTauriRuntime()) return uploadBrowserPreparedEncryptedMediaTransfer(transferId, uploadUrl);
  return invoke<UploadedEncryptedMediaTransfer>("upload_prepared_encrypted_media_transfer", {
    request: { transferId, uploadUrl }
  });
}

export function receiveEncryptedMediaTransfer(request: {
  transferId: string;
  mediaId: string;
  recipientDeviceId: string;
  envelope: string;
  downloadUrl: string;
  expectedSourceSha256: string;
  expectedCiphertextSha256: string;
  expectedCiphertextSize: number;
  fileName: string;
  mimeType: string;
}) {
  if (!isTauriRuntime()) return receiveBrowserEncryptedMediaTransfer(request);
  return invoke<{ mediaId: string; byteSize: number; sha256: string; storageState: "durable" }>(
    "receive_encrypted_media_transfer",
    { request }
  );
}

export function receiveEncryptedBinaryTransfer(request: {
  transferId: string;
  contentId: string;
  recipientDeviceId: string;
  envelope: string;
  downloadUrl: string;
  expectedSourceSha256: string;
  expectedCiphertextSha256: string;
  expectedCiphertextSize: number;
}) {
  if (!isTauriRuntime()) {
    return receiveBrowserEncryptedBytesTransfer({
      mediaId: request.contentId,
      recipientDeviceId: request.recipientDeviceId,
      envelope: request.envelope,
      downloadUrl: request.downloadUrl,
      expectedSourceSha256: request.expectedSourceSha256,
      expectedCiphertextSha256: request.expectedCiphertextSha256,
      expectedCiphertextSize: request.expectedCiphertextSize
    });
  }
  return invoke<ReceivedEncryptedBinaryTransfer>("receive_encrypted_derived_transfer", {
    request
  });
}
